// PHA-575: rendered inside the AiInsightDrawer.
// MA action: actionable, may include a suggested call script.
// MD action: optional; suggested_orders are INERT chips that open the
// underlying source via citation deep-link. They never trigger an order.

import React from 'react';
import { Box, Card, Flex, Heading, Text, Badge } from '@radix-ui/themes';
import type { AiInsightAction } from '../types';

interface ActionBlockProps {
  title: string;
  action: AiInsightAction;
  variant: 'ma' | 'md';
}

const urgencyColor = (urgency: AiInsightAction['urgency']): 'red' | 'orange' | 'gray' => {
  if (urgency === 'today') return 'red';
  if (urgency === 'this_week') return 'orange';
  return 'gray';
};

const urgencyLabel = (urgency: AiInsightAction['urgency']): string => {
  if (urgency === 'today') return 'Today';
  if (urgency === 'this_week') return 'This week';
  return 'This month';
};

export const ActionBlock: React.FC<ActionBlockProps> = ({ title, action, variant }) => {
  return (
    <Card variant="surface" style={{ borderLeft: variant === 'md' ? '3px solid var(--purple-9)' : '3px solid var(--blue-9)' }}>
      <Flex direction="column" gap="2">
        <Flex align="center" justify="between" gap="2">
          <Heading size="2">{title}</Heading>
          <Badge color={urgencyColor(action.urgency)} variant="soft">
            {urgencyLabel(action.urgency)}
          </Badge>
        </Flex>
        <Text size="2">{action.summary}</Text>
        {action.steps && action.steps.length > 0 && (
          <Box asChild>
            <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 18 }}>
              {action.steps.map((step, idx) => (
                <li key={idx}>
                  <Text size="2">{step}</Text>
                </li>
              ))}
            </ul>
          </Box>
        )}
        {action.suggested_call_script && (
          <Box style={{ marginTop: 4 }}>
            <Text size="1" color="gray" weight="bold">
              Suggested call script
            </Text>
            <Text
              size="2"
              as="div"
              style={{
                marginTop: 4,
                whiteSpace: 'pre-wrap',
                fontStyle: 'italic',
                background: 'var(--gray-2)',
                padding: 8,
                borderRadius: 4,
              }}
            >
              {action.suggested_call_script}
            </Text>
          </Box>
        )}
        {variant === 'md' && action.suggested_orders && action.suggested_orders.length > 0 && (
          <Box style={{ marginTop: 4 }}>
            <Text size="1" color="gray" weight="bold">
              Suggested orders (review only — does not place an order)
            </Text>
            <Flex gap="2" wrap="wrap" style={{ marginTop: 4 }}>
              {action.suggested_orders.map((order, idx) => (
                <Badge key={idx} color="purple" variant="soft">
                  {order}
                </Badge>
              ))}
            </Flex>
          </Box>
        )}
      </Flex>
    </Card>
  );
};

export default ActionBlock;
